import React from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Autoplay } from 'swiper'
import { VideoPlayer } from 'kunstmuseum-components'

import SEO from '../seo'
import Layout from '../components/layout'
import ExhibitionHead from '../components/exhibition-head'
//import IntroSection from '../components/intro-section'
import IntroText from '../components/intro-text'
import RichText from '../components/rich-text'
import Chapter from '../components/chapter'
import Quote from '../components/quote'
import ParallaxBackground from '../components/parallax-background'
import VideoBackground from '../components/video-background'
import SimpleImage from '../components/simple-image'
import CoverflowSlider from '../components/coverflow-slider'
import ExpandableText from '../components/expandable-text'
import { ClickableImage, ClickableImageItem, ClickableImageItemOverlayColumn } from '../components/clickable-image'
import AudioWrapper from '../components/audio-wrapper'
import InfoPanel from '../components/info-panel'
import Feedback from '../components/feedback'

import HeroImage from '../images/hero.jpg'
import Megazeichen from '../images/Megazeichen_V.jpg'
import PlatonsMirrorBackgroundVideo from '../videos/210511_KWM_by_Kruszewski_5MK_6416_background.mp4'
import PlatonsMirrorBackgroundVideoThumb from '../images/20210505_KMW_by_Kruszewski_360.jpg'
import PlatonsMirrorParallax from '../images/Personen_Schatten_Parallax.jpg'
import BrocaReMix1 from '../images/20210505_KMW_by_Kruszewski_500.jpg'
import Blitz1 from '../images/Blitz3 01F33720.jpg'
import Blitz2 from '../images/Blitz4 01F343DC.jpg'
import Blitz3 from '../images/Blitz6 01F358C3.jpg'
import Blitz4 from '../images/Blitz8 01F3701A.jpg'
import Blitz5 from '../images/Blitz10 01F38FE9.jpg'
import Blitz6 from '../images/Blitz18 01F3F936.jpg'
import Blitz7 from '../images/Blitz22 01F430F8.jpg'
import Blitz8 from '../images/Blitz28 01F47567.jpg'
import Blitz9 from '../images/Blitz39 01F4FBA6.jpg'
import FiveSuns from '../images/IMG_4275.jpg'
import Kopernikus from '../images/Illustration_Kopernikus.jpg'
import MagazinDes from '../images/Magazin 20 Jahrhundert_02.jpg'
import ResearchDeskCollage from '../images/research_desk_collage.jpg'
import BerlinFloater from '../images/IMG_2260.jpg'
import MakingOfMnemosyne from '../images/210308_KMW_by_Kruszewski__MK_1693_V5.jpg'
import FivePlanetsBackgroundVideo from '../videos/210511_KWM_by_Kruszewski_5MK_6398_background.mp4'
import FivePlanetsBackgroundVideoThumb from '../images/20210505_KMW_by_Kruszewski_607.jpg'
import PublicPrepositionVideoThumb from '../images/public_preposition_02_720p.jpg'
import PublicPrepositionVideo from '../videos/public_preposition_02_720p.mp4'
import ExhibitionTrailer from '../videos/Wolfsburg_Kuball_Trailer_210507_3_720p.mp4'
import ExhibitionTrailerPoster from '../images/ExhibitionTrailerThumbnail.jpg'
import MakingOfMnemosyneBackgroundAudio from '../audios/Sound_Making of Menmosyne.mp3'

SwiperCore.use([Autoplay])

export default function Curatorial() {
  return (
    <Layout>
      <SEO lang="de" title="Mischa Kuball. ReferenzRäume" />
      <ExhibitionHead
        backgroundColor="#000"
        backgroundImageRef={HeroImage}
        lightText
        title="Mischa Kuball"
        subtitle="ReferenzRäume"
        startDate="8. 5. "
        endDate="19. 9. 2021"
      />
      <section className="introduction">
        <IntroText>
          <p>
            Von Platons Höhle zu Galileos Sternen spannt sich der thematische Bogen der Ausstellung <em>Mischa Kuball. ReferenzRäume</em> (8.5.-19.9.2021).
            Kommen Sie mit durch beeindruckende Rauminstallationen, lassen Sie sich vom Spiel des Lichts verführen und folgen Sie Mischa Kuballs politischen,
            philosophischen und naturwissenschaftlichen Fragestellungen in der ersten Retrospektive des Konzeptkünstlers Mischa Kuball im Kunstmuseum Wolfsburg!
          </p>
        </IntroText>
        <SimpleImage
          imageRef={Megazeichen}
          title="Megazeichen"
          artist="Mischa Kuball"
          year="1990"
          credit="Lichtinstallation Mannesmann-Hochhaus, Düsseldorf, © Archive Mischa Kuball, Düsseldorf / VG Bild-Kunst, Bonn 2021, Foto: Ulrich Schiller, Düsseldorf"
          horizontal
        >
          Ein Hochhaus, das wochenlang kryptische Lichtzeichen in den nächtlichen Stadtraum hinausstrahlt: Es sind vor allem starke Interventionen im öffentlichen Raum wie diese, die Mischa Kuball (*1959) international bekannt gemacht haben.
          Mit seinen Installationen, Performances, Fotografien und Projektionen erforscht Mischa Kuball urbane Situationen, architektonische Strukturen und gesellschaftliche Wahrnehmungsmuster. Dabei verfolgt er einen Raum und Zeit übergreifenden Netzwerkgedanken. Mischa Kuballs Werke thematisieren die immer aktueller werdende Fragestellung nach der Unterscheidung zwischen Schein und Sein bzw. Abbild und Wirklichkeit. Er hinterfragt, was ein Bild sein und wie es produziert werden kann, und differenziert zwischen der Realität und den Möglichkeiten ihrer Reproduktion.
        </SimpleImage>
        <Quote cite="Mischa Kuball" maxWidth="800px" alignRight>
          1977 startete ich mit unstillbarer Begeisterung mit dem Medium Licht -
          aus heutiger Sicht bin ich wohl vom ‚Lichteuphoriker‘ zum 'Lichtskeptiker’ geworden.
        </Quote>
      </section>
      <Chapter title="<i>platon’s mirror</i>, 2011" anchor="platonsmirror">
        <VideoBackground 
          backgroundVideoRef={PlatonsMirrorBackgroundVideo} 
          backgroundVideoPoster={PlatonsMirrorBackgroundVideoThumb}
          artist="Mischa Kuball"
          title="platon’s mirror"
          year="2011"
          credit="ortsspezifische Videoinstallation, Video (16:9), Farbe, Autopolsystem, Spiegelfolie, ohne Ton, Raum 1/4, Courtesy der Künstler und Daniel Marzona, Berlin © Archiv Mischa Kuball, Düsseldorf / VG Bild-Kunst, Bonn 2021, Video: Marek Kruszewski"
        >
          <IntroText>
            <p>
              Die Idee hinter der Arbeit <em>platon’s mirror</em> basiert auf Platons Höhlengleichnis, einem der einflussreichsten Texte der europäischen Literatur.
              In dem Text des griechischen Philosophen Platon, der im fünften bis vierten Jahrhundert vor Christus lebte, wird zwischen zwei Formen der Realität unterschieden:
              der sichtbaren bzw. vermeintlichen Realität und der wahren Realität der Ideen.
            </p>
          </IntroText>
        </VideoBackground>
        <RichText>
          <p>
            Bei dem Höhlengleichnis geht es um das Wissen und Nichtwissen der Menschen und der Bewusstwerdung neuer Erkenntnisse.
            Mithilfe eines über einen Projektor ausgestrahlten Videos und Silberfolie konstruiert Mischa Kuball einen Raum, der in Analogie zu Platons Höhlengleichnis steht.
            Ähnlich den Schatten in Platons Höhlengleichnis sind hier nur Reflexionen wahrnehmbar. Dabei entstehen und wandeln sich die Formen in unterschiedlichen Rhythmen.
            Es sind Lichtspiele, die sich nicht eindeutig interpretieren lassen: Was ist Videoaufnahme, was ist reale Spiegelung der Personen im Raum?
          </p>
        </RichText>
        <ParallaxBackground
          backgroundImageRef={PlatonsMirrorParallax}
          darkenedBackground
          textColor="#fff"
        >
          <RichText>
            <p>
              In Platons Höhlengleichnis sehen die in einer Höhle Gefangenen nur die Schatten der Dinge, die hinter ihnen über einer Mauer hervorragen.
              Die Gefangenen sind mit dem Blick zur Wand gefesselt. Da sie schon ihr gesamtes Leben in der Höhle verbracht haben, werden die Schatten als real empfunden.
              Eines Tages kann sich eine Person aus der Gefangenschaft in der Höhle befreien.
              Sie erblickt das Tageslicht und versteht zunächst nicht, was die Realität ist. Erst mit der Zeit setzt eine Gewöhnung ein und der Mensch versteht,
              dass die Schatten in der Höhle nur ein Teil der Wirklichkeit sind. Müsste er dorthin zurück, würde er nichts mehr erkennen,
              und die anderen Gefangenen würden sich über seine Erkenntnisse lustig machen und ihn für verrückt halten.
            </p>
          </RichText>
        </ParallaxBackground>
      </Chapter>
      <Chapter title="<i>broca Re:Mix</i>, 2007" anchor="brocaremix">
        <IntroText>
          <p>
            Zahllose Buchstaben und Ziffern werden mittels sechs unterschiedlich schnell rotierender Projektoren an die Wände geworfen.
            Die Suche nach einem Sinn ist ergebnisoffen: Das Erscheinen der Buchstaben unterliegt einem Zufallsprinzip.
          </p>
        </IntroText>
        <Quote cite="Mischa Kuball" alignRight>
          Wir wollen immer erkennen, wir wollen immer verstehen.
        </Quote>
        <ClickableImage
          imageRef={BrocaReMix1}
          lightItems
          title="broca Re:Mix"
          artist="Mischa Kuball"
          year="2007"
          credit="Rauminstallation, 10 Metallobjekte, 6 Diaprojektoren mit je 81 Dias, 6 Linsen, 6 Drehteller, Courtesy der Künstler und Daniel Marzona, Berlin ©  Archiv Mischa Kuball, Düsseldorf / VG Bild-Kunst, Bonn 2021"
        >
          <ClickableImageItem topPosition="85" leftPosition="10" openInside minWidth="350px">
            Im Titel der Arbeit spielt Mischa Kuball auf das entsprechende Areal in der Großhirnrinde an:
            Das Broca-Areal ist eine Hirnregion, in der die Fähigkeit zur Sprache ausgebildet wird.
            Sie ist nach dem französischen Chirurgen Paul Broca (1824—1880) benannt.
          </ClickableImageItem>
          <ClickableImageItem topPosition="45" leftPosition="85" openInside openLeft minWidth="350px">
            Die plastischen Elemente im Raum stellen die in dreidimensionale Formen übertragenen,
            digitalisierten Hirnströme des Künstlers da, die gemessen wurden, während er sich die Ziffern Null bis Neun vorstellte.
          </ClickableImageItem>
        </ClickableImage>
        <RichText>
          <p>
            Mischa Kuball untersucht in dieser Rauminstallation das komplexe System zwischen Sprachbildung und Schrift.
            Somit verbinden sich auf unterschiedlichen Ebenen das Fassen, Ausdrücken und Darstellen von Gedanken als Vorstufe zur Kommunikation.
            Die Besucher*innen werden aktiv in diesen Prozess einbezogen, indem sie automatisch versuchen, die Zeichen zu kombinieren und über die einzelnen Buchstaben nach Wörtern und Inhalten suchen.
            Das Zufallsprinzip, das dem Erscheinen der Zeichen zugrunde liegt, lässt dies jedoch nicht gelingen.
          </p>
        </RichText>
      </Chapter>
      <Chapter title="<i>platon’s mirror_image apparatus_ct_blitz</i>, 2011" anchor="platonsmirrorimageapparatusctblitz">
        <IntroText>
          <p>
            Nachdem Mischa Kuball zunächst ohne künstlerische Ambitionen in einer privaten radiologischen Praxis Forschungen an seinem Gehirn betrieb,
            platzierte er einige Jahre später bildproduzierende Apparate in einem Computertomographen (CT).
            Mithilfe eines CTs hat er zahlreiche Schwarz-Weiß-Abzüge von bildgebenden Apparaturen wie Foto- und Filmkameras, Objektiven sowie Blitzgeräten geschaffen,
            die die unterschiedlichen Artefakte der Aufnahmen zeigen. Die Arbeit steht im Bezug zu der Rauminstallation <a href="#platonsmirror"><em>platon‘s mirror</em></a>.
            Beide beschäftigen sich mit unerwarteten Erscheinungen und dem, was nicht sichtbar ist.
            Das Ergebnis, das sich mit jeder Aufnahme ändert, wirft die Frage nach der Wirklichkeit und ihrer Reproduzierbarkeit auf.
          </p>
        </IntroText>
        <CoverflowSlider
          initialSlide={3}
          title="platon’s mirror_image apparatus_ct_blitz"
          artist="Mischa Kuball"
          year="2011"
          credit="42 Schwarz-Weiß-Fotografien auf Artpaper (ct-Fotografie), je 57,6 × 66,6 cm, Courtesy der Künstler und Daniel Marzona, Berlin copyright Archiv Mischa Kuball, Düsseldorf / VG Bild-Kunst, Bonn 2021"
        >
          <SwiperSlide>
            <img src={Blitz1} height="600" />
          </SwiperSlide>
          <SwiperSlide>
            <img src={Blitz2} height="600" />
          </SwiperSlide>
          <SwiperSlide>
            <img src={Blitz3} height="600" />
          </SwiperSlide>
          <SwiperSlide>
            <img src={Blitz4} height="600" />
          </SwiperSlide>
          <SwiperSlide>
            <img src={Blitz5} height="600" />
          </SwiperSlide>
          <SwiperSlide>
            <img src={Blitz6} height="600" />
          </SwiperSlide>
          <SwiperSlide>
            <img src={Blitz7} height="600" />
          </SwiperSlide>
          <SwiperSlide>
            <img src={Blitz8} height="600" />
          </SwiperSlide>
          <SwiperSlide>
            <img src={Blitz9} height="600" />
          </SwiperSlide>
        </CoverflowSlider>
        <InfoPanel>
          <p>
            Bei einem medizinischen Röntgenvorgang werden normalerweise alle metallischen Gegenstände abgelegt,
            da ansonsten der Röntgenstrahl durch das Metall abgelenkt und die Röntgenaufnahme durch „Lichtblitze“, sogenannte Artefakte, gestört würde.
            Das Entstehen solcher unvorhersehbaren und einzigartigen Lichtspuren, die nicht wiederholbar sind, nutzt Mischa Kuball bei seiner Serie, deren Bilder ausschließlich aus solchen Artefakten bestehen.
          </p>
        </InfoPanel>
      </Chapter>
      <Chapter title="<i>five suns / after Galileo</i>, 2018" anchor="fivesunsaftergalileo">
        <IntroText>
          <p>
            Die Idee der Installation <em>five suns / after Galileo</em> beruht auf einer der wichtigsten astronomischen Entdeckungen des italienischen Universalgelehrten Galileo Galilei (1564—1641), die dieser mit einem selbstentwickelten Fernrohr machte.
            Mit der Referenz auf Galilei und dessen Entdeckungen von Sonnenflecken sowie der daraus folgenden Bestätigung eines heliozentrischen Weltbildes, eröffnet Mischa Kuball in seiner Inszenierung einen neuen Erfahrungsraum.
            Das auf fünf sich drehende, farbige Scheiben gerichtete Licht erzeugt faszinierende Farbwirkungen im Raum und lässt die Betrachter*innen darüber nachdenken, dass Wahrheit oftmals relativ ist und sich je nach Erkenntnissen und Ideologien ändern kann.
          </p>
        </IntroText>
        <ClickableImage
          imageRef={FiveSuns}
          artist="Mischa Kuball"
          title="five suns / after Galileo"
          year="2018"
          credit="Lichtinstallation, Profiler, Dia, 5 Plexiglasplatten je 60 × 60 × 0,3 cm, 5 Motoren, Maße variabel, Sammlung Monika Schnetkamp © Archiv Mischa Kuball, Düsseldorf / VG Bild-Kunst, Bonn 2021"
        />
        <InfoPanel>
          <h4>Was sah Galileo durchs Fernrohr?</h4>
          <ExpandableText>
            <p>
              Mithilfe eines Fernrohres, das Galilei weiterentwickelt hatte, entdeckte er zwischen 1610 und 1611 dunkle, sich bewegende und verändernde Flecken auf der Sonnenscheibe. 
              Weiterhin stellte er fest, dass die Milchstraße aus unzähligen Sternen besteht und dass den Planeten Jupiter vier Monde umkreisen. 
              Er entdeckte zudem, dass die Venus Lichtphasen zeigt, wie wir sie vom Mond kennen und schloss daraus, dass sie um die Sonne kreisen muss, nicht um die Erde. 
              Eindeutig zuordnen konnte Galilei seine Beobachtungen noch nicht, aber sie stützten das durch Nikolaus Kopernikus beschriebene, aber damals noch nicht bewiesene heliozentrische Weltbild.
            </p>
          </ExpandableText>
        </InfoPanel>
        <InfoPanel sideImageRef={Kopernikus}>
          <h4>Wer war Nikolaus Kopernikus?</h4>
          <ExpandableText>
            <p>
              Nikolaus Kopernikus (1473—1543) war Arzt und Astronom. Im Jahr 1543 beschrieb er ein Weltbild, in dem die Sonne als das ruhende Zentrum des Universums gilt und die Erde ein Planet sei, der sich um seine eigene Achse drehe und sich zudem wie die anderen Planeten um die Sonne bewege. Dieses Weltbild wird als heliozentrisches Weltbild bezeichnet. Dies widersprach den Dogmen der christlichen Kirche, welche die Erde als Scheibe und Mittelpunkt des Universums ansah.
            </p>
          </ExpandableText>
        </InfoPanel>
      </Chapter>
      <Chapter title="<i>Magazin des 20. Jahrhunderts</i>, 1989/90" anchor="magazindesjahrhundertsmagazineofthethcentury">
        <ClickableImage
          imageRef={MagazinDes}
          artist="Mischa Kuball"
          title="Magazin des 20. Jahrhunderts"
          year="1989/90"
          credit="Metallschrank mit 4 Schubladen, 4 Diaprojektoren mit je 81 Dias,154 × 52 × 60 cm, Gottfried Hafemann, Wiesbaden © Archiv Mischa Kuball, Düsseldorf / VG Bild-Kunst, Bonn 2021"
          lightItems
        >
          <ClickableImageItem topPosition="85" leftPosition="45" minWidth="275px" openLeft openInside>Zu sehen ist ein schwarzer, gewöhnlicher Aktenschrank aus Metall mit vier Schubladen.</ClickableImageItem>
          <ClickableImageItem topPosition="68" leftPosition="52" minWidth="350px" openLeft openInside>Jedes Dia zeigt ein Kunstwerk aus Sammlungen berühmter Museen. Die Abbildungen der ausgewählten und als bedeutend geltenden Kunstwerke stehen beispielhaft für den Kunstkanon verschiedener Kulturkreise.</ClickableImageItem>
          <ClickableImageItem topPosition="52" leftPosition="56" minWidth="300px" openLeft openInside>Die geöffneten Schubladen des hier ausgestellten Schrankes enthalten vier Diaprojektoren mit je 81 Dias.</ClickableImageItem>
          <ClickableImageItem topPosition="38" leftPosition="53" minWidth="325px" openLeft openInside>Derartige Aktenschränke waren noch vor wenigen Jahren in vielen Büros zu finden und stehen für eine etablierte Ordnungsstruktur.</ClickableImageItem>
          <ClickableImageItem topPosition="20" leftPosition="50" minWidth="375px" openLeft openInside>Durch die asynchron wechselnden Bilder ergeben sich immer neue Konstellationen, die unterschiedlichste Assoziationen wecken. So stellt sich gerade heute die Frage, welche Bilder nicht Teil des festgelegten Kanons sind und warum sie von einer etablierten Ordnung ausgeschlossen werden.</ClickableImageItem>
        </ClickableImage>
      </Chapter>
      <ParallaxBackground
        backgroundImageRef={ResearchDeskCollage}
        darkenedBackground
        artist="Mischa Kuball"
        title="research_desk_Nolde/Kritik/Kuball"
        year="2020"
        credit="22 Unikate, Digitaldrucke auf Aluminium-Verbundplatte, je 160 × 90 cm © Archiv Mischa Kuball, Düsseldorf / VG Bild-Kunst, Bonn 2021"
        noMargin
      >
        <Chapter title="<i>research_&shy;desk_ Nolde/&shy;Kritik/&shy;Kuball</i>, 2020" anchor="researchdesknoldekritikkuball">
          <IntroText>
            <p>
                Mit der Arbeit <em>research_desk_Nolde/Kritik/Kuball</em> setzt sich Mischa Kuball kritisch mit der Person und dem Werk Emil Noldes auseinander.
                Die Perspektive auf Noldes Werke ist ungewöhnlich, da keines seiner Werke direkt gezeigt oder reproduziert wird.
                Kuballs Arbeit entspricht vielmehr einer vielschichtigen, offenen, kritischen und gleichzeitig ästhetischen Beleuchtung.
                Sie regt dazu an, die Prozesse hinter den Werken wahrzunehmen, zu erkennen und kritisch zu hinterfragen.
                Das Projekt <em>Emil Nolde - a critical Approach by Mischa Kuball</em> wurde von der Draiflessen Collection in Mettingen initiiert und 2020 gezeigt. 
              </p>
          </IntroText>
          <InfoPanel>
            <p>
              Emil Nolde (1867—1956) gilt nicht nur als einer der großen Maler des deutschen Expressionismus,
              sondern auch als einer der ambivalentesten deutschen Künstler des letzten Jahrhunderts.
              Der freie Umgang mit Farbe und markanten Formen sowie das Auflösen der traditionellen Perspektive sind charakteristisch für sein Werk und die expressionistische Malerei. Seine Arbeiten entsprachen aber weder der Ästhetik noch der Ideologie der Nationalsozialisten und wurden im Nationalsozialismus als „entartet“ eingestuft. Nach dem Zweiten Weltkrieg hatte sich der Maler mithilfe namhafter Kunsthistoriker*innen als Opfer der Nationalsozialisten stilisiert, ungeachtet der Tatsache, dass Nolde Mitglied der NSDAP und bekennender Antisemit war.
              Erst durch Forschungen der letzten Jahre sind neue Erkenntnisse ins öffentliche Bewusstsein gerückt und verändern den Blick auf seine Person und sein Werk.
              </p>
          </InfoPanel>
        </Chapter>
      </ParallaxBackground>
      <Chapter title="<i>Marfa Floater (indoor/outdoor)</i>, 2009" anchor="marfafloaterindooroutdoor">
        <IntroText>
          <p>
            Die Installation besteht aus zwei Videos und mehreren Zeichnungen.
            Entstanden ist die Videoarbeit mit dem Titel <em>Marfa Floater</em> im Jahr 2009 in Marfa,
            einem kleinen Ort in der Wüste von Texas und mit der Donald Judd Foundation ein Zentrum der US-amerikanischen Minimal Art.
            Der „Hauptakteur“ des Videos ist eine hauchdünne Rettungsdecke aus metallisiertem Polyester.
            Die vom Wüstenwind angetriebene, nahezu schwerelos durch die Gegend schwebende Decke ist losgelöst von der eigentlich zugedachten Verwendung und
            erscheint als Gegenentwurf zu den statisch-kubischen Werken des männlich dominierten Minimalismus.
          </p>
        </IntroText>
        {/*<SimpleImage
          artist="Mischa Kuball"
          title="Marfa Floater (indoor/outdoor)"
          year="2009"
          credit="2 Monitore, Videos, ca. 25 Min., Ton sowie Mischa Kuball: Marfa Floater_silver/gold, 2009,5 Zeichnungen auf Endlospapier, je 32,4 × 42 cm, beide Werke Courtesy der Künstler und Daniel Marzona, Berlin &copy; Archiv Mischa Kuball, Düsseldorf / VG Bild-Kunst, Bonn 2021."
        />*/}
      </Chapter>
      <Chapter title="<i>Berlin Floater (outdoor/drone)</i>, 2019" anchor="berlinfloateroutdoordrone">
        <IntroText>
          <p>
            Zehn Jahre später wurde die Performance ein zweites Mal durchgeführt. 
            Aus der Vogelperspektive mit einer Drohne gefilmt, schwebte 2019 wieder eine Rettungsdecke durch die Landschaft; dieses Mal jedoch nicht in Texas, sondern mitten in Berlin.
          </p>
        </IntroText>
        <SimpleImage
          imageRef={BerlinFloater}
          artist="Mischa Kuball"
          title="Berlin Floater (outdoor/drone)"
          year="2019"
          credit="Rauminstallation, Videoprojektion, 9:07 Min., 1 Monitor verbunden mit einer Kamera, Maße variabel cm © Archiv Mischa Kuball, Düsseldorf / VG Bild-Kunst, Bonn 2021"
          horizontal
        >
          Durch einen Ventilator in Bewegung versetzt, schwebte die glänzende Rettungsdecke aus Mischa Kuballs Galerie kommend die Friedrichstraße entlang. 
          So entstand die im Großformat gezeigte Videoarbeit mit dem Titel <em>Berlin Floater</em>. 
          An beiden Orten – in Marfa und in Berlin - fand die "Performance" sowohl im Innenraum als auch Außen statt und stellt so eine Verbindung zwischen beiden Orten her. 
          Die Rettungsdecke wird auch hier ihrer ursprünglichen Zweckbindung enthoben und verweist auf die Verletzlichkeit, das Ausgeliefertsein des Menschen (im öffentlichen Raum) und stellt über allgemeine Notfälle hinaus auch Assoziationen zu Obdachlosigkeit und Flucht her.
        </SimpleImage>
      </Chapter>
      <Chapter title="<i>making of Mnemosyne (after Aby Warburg)</i>, 2021" anchor="makingofmnemosyneafterabywarburg">
        <AudioWrapper audioRef={MakingOfMnemosyneBackgroundAudio}>
          <IntroText>
            <p>
              Kulturgeschichtliche Fragestellungen reflektiert Mischa Kuball in seiner neuesten,
              eigens für diese Ausstellung konzipierten Arbeit <em>making of Mnemosyne (after Aby Warburg)</em> von 2021.
              Dabei geht es um die Methode des vergleichenden Sehens, die dem berühmten Bilderatlas Mnemosyne von Aby Warburg (1866–1929) zugrunde liegt.
            </p>
          </IntroText>
          <ClickableImage
            imageRef={MakingOfMnemosyne}
            artist="Mischa Kuball"
            title="making of Mnemosyne (after Aby Warburg)"
            year="2021"
            credit="2-Kanal-Videoinstallation, Projektion (16:9), je 45 Min., Farbe, 2 Projektionsflächen je 300 × 170 × 10 cm, Ed. 1/3, Maße variabel © Archiv Mischa Kuball, Düsseldorf / VG Bild-Kunst, Bonn 2021"
            lightItems
          >
              <ClickableImageItem topPosition="45" leftPosition="80" minWidth="300px" openLeft openInside>Das senkrecht projizierte Video zeigt Tafeln aus dem Mnemosyne-Atlas Aby Warburgs, die waagerechte Projektion die Arbeit an der Rekonstruktion der Bildtafeln.</ClickableImageItem>
              <ClickableImageItem topPosition="20" leftPosition="15" minWidth="300px" openInside>Die Installation <em>making of Mnemosyne (after Aby Warburg)</em> besteht aus zwei Videoprojektionen auf jeweils drei Meter hohe Wandstücke, von denen eines auf dem Boden liegt.</ClickableImageItem>
          </ClickableImage>
          <InfoPanel>
            <p>
              Aby M. Warburg war Kunsthistoriker und Kulturwissenschaftler.
              Er lebte von 1866 bis 1929 und gilt als einer der Pioniere der modernen Kunst- und Bildwissenschaften.
              Sein Konzept beruht, über die Zeit und viele Epochen hinweg, auf der Ähnlichkeit von Bildern und ihren Motiven.
              Sein <em>Bilderatlas Mnemosyne</em> zeigt wiederkehrende visuelle Themen und Muster von der Antike über die Renaissance bis zur Gegenwartskultur.
              Der Atlas umfasst 63 Tafeln und knapp 1.000 Einzelstücke.
              Überwiegend Fotografien, aber auch Illustrationen aus Büchern, Originalgrafiken, Zeitungsseiten oder Briefmarken hat Warburg auf seinen Bildtafeln platziert.
              Benannt nach Mnemosyne, der griechischen Göttin der Erinnerung und Mutter der Musen,
              dient der Bilderatlas als ein Instrument der Erkenntnis auf der Grundlage des vergleichenden Sehens.
            </p>
          </InfoPanel>
          <RichText>
            <p>
              Die für die Ausstellung im Kunstmuseum Wolfsburg konzipierte Arbeit <em>making of Mnemosyne (after Aby Warburg)</em> weist inhaltliche Verbindungen
              zu den Werken <em><a href="#researchdesknoldekritikkuball">research_desk_Nolde/Kritik/Kuball</a></em> und <em><a href="#magazindesjahrhundertsmagazineofthethcentury">Magazin des 20. Jahrhunderts</a></em> auf.
              Mit den genannten Arbeiten setzt sich Mischa Kuball kritisch mit der Erforschung von kunst- und kulturwissenschaftlichen (Rezeptions-)Zusammenhängen auseinander.
              Dabei führen die Methode des Vergleichs und der Perspektivwechsel stets zu neuen Erkenntnissen und stellen die kritiklose Annahme von Realitäten oder Wahrheiten infrage.
            </p>
          </RichText>
        </AudioWrapper>
      </Chapter>
      <VideoBackground 
        backgroundVideoRef={FivePlanetsBackgroundVideo} 
        backgroundVideoPoster={FivePlanetsBackgroundVideoThumb} 
        noMargin
        artist="Mischa Kuball"
        title="five planets"
        year="2015"
        credit="raumbezogene Lichtinstallation, 5 Spiegelkugeln, Drehmotoren, Gobostrahler, Maße variabel © Archiv Mischa Kuball, Düsseldorf / VG Bild-Kunst, Bonn 2021, Video: Marek Kruszewski"
      >
        <Chapter title="<i>five planets</i>, 2015" anchor="fiveplanets">
          <IntroText>
            <p>
              Mischa Kuball versucht mit der Installation <em>five planets</em>, das Unfassbare und Unbegreifliche unseres Universums erfahrbar zu machen.
              Gleichzeitig spielt er auf die Erkenntnisentwicklung des Menschen an, der im Zuge seiner Evolution, insbesondere seit der Aufklärung, zum Weltraumreisenden geworden ist.
              Darüber hinaus stellt er der klassischen Lichtmetaphorik und ihrer Idee des aufklärerischen Denkens die Erfahrung sozialer und politischer Räume gegenüber.
            </p>
          </IntroText>
          <RichText>
            <p>
              Das Zentrum der Installation bilden fünf rotierende Diskokugeln.
              Ihre verspiegelten Flächen werden durch Lichtschablonen angestrahlt,
              sodass sie die Namen von fünf Planeten unseres Sonnensystems als Lichtfetzen an die Wände und auf den Boden des Raumes werfen.
              Die ineinandergreifenden Spiegelungen der Diskokugeln vernetzen den dunklen Raum und suggerieren die Präsenz von Venus, Mars, Saturn, Jupiter und Merkur.
              Mit ihren Reflexionen, die sich im Raum ausdehnen und überlagern, entsteht ein eigenes Universum.
            </p>
          </RichText>
        </Chapter>
      </VideoBackground>
      <Chapter title="<i>public preposition (Materialsammlung)</i>, seit 2009" anchor="publicprepositionmaterialsammlung">
        <IntroText>
          <p>
            Unter welchen Bedingungen entsteht Öffentlichkeit?
            Worauf basiert öffentlicher Raum und wie verhält sich dieser, wenn Kunst in ihn eingreift?
            Diese und weitere Fragen nimmt Mischa Kuball mit seinen Werken, Interventionen und Performances im öffentlichen Raum auf und vermittelt Antworten.
            Schon seit vielen Jahren erforscht er anhand seiner Projekte die Wechselwirkung von Kunst, Öffentlichkeit und Ort.
          2015 erschien hierzu die umfassende Materialsammlung <em>puplic preposition</em> als Publikation.
          </p>
        </IntroText>
        <RichText>
          <ExpandableText>
            <p>
              Die seit 2009 entwickelte Werkreihe <em>public preposition</em> untersucht öffentliche Räume und die menschlichen Handlungen in ihnen,
              stellt unsere Wahrnehmung von scheinbar vertrauten Umgebungen infrage und schafft Momente der Irritation. Mischa Kuball arbeitete in diesem Kontext unter anderem in Venedig, Marfa (Texas), Toronto, Bern, Katowice, Christchurch, Thessaloniki, Berlin, Jerusalem, Sydney und Boston.
              Auch die in dieser Ausstellung gezeigte Arbeit Marfa Floater / Berlin Floater wurde ursprünglich für den (halb)öffentlichen Raum konzipiert
            </p>
          </ExpandableText>
        </RichText>
        <VideoPlayer width="1000px" src={PublicPrepositionVideo} poster={PublicPrepositionVideoThumb} />
        {/*<TitleCredits 
          artist="Mischa Kuball"
          title="public preposition (Materialsammlung)"
          year="seit 2009"
          credit="2 Transparente, Video, Monitore, Laptop, Plakate, Unterlagen, Fotografien, Megafon, 2 Projektionen, 2 Beamer, Maße variabel © Archiv Mischa Kuball, Düsseldorf / VG Bild-Kunst, Bonn 2021"
        />*/}
        <RichText>
          <p>
            Der fortlaufende Charakter der public prepositions als offene Projektreihe eröffnet die Möglichkeit,
            aus der Dokumentation von Projekten neue Arbeiten oder Publikationen zu entwickeln.
            Einzelne Projekte, die Mischa Kuball nicht als abgeschlossene Werke versteht, können nicht nur im Rahmen einer Ausstellung präsentiert und re-installiert werden;
            sein Konzept sieht vor, diese auch im urbanen Umfeld der ausstellenden Institutionen zu re-inszenieren, ortsspezifisch zu modifizieren und weiterzuentwickeln.
          </p>
        </RichText>
        <Quote cite="Mischa Kuball" maxWidth="900px">
          In jedem Projekt müssen andere Fragen gestellt werden, weil eine Installation nicht nur ortsspezifisch, sondern auch kontextspezifisch ist.
        </Quote>
      </Chapter>
      <VideoPlayer width="1000px" src={ExhibitionTrailer} poster={ExhibitionTrailerPoster} />
      <ParallaxBackground backgroundImageRef={HeroImage} darkenedBackground noMargin>
        <Feedback submitUri="/feedback/collector.php" curatorialId="2">
          <h3>Wie hat Ihnen dieses Curatorial gefallen?</h3>
        </Feedback>
      </ParallaxBackground>
    </Layout>
  )
}
